<template>
  <v-container class="event-survey" fluid v-if="survey">
    <v-form v-model="isFormValid">
      <v-row>
        <v-col>
          <v-text-field readonly
                        :label="$t('admin_event_survey.survey_enabled')"
                        :value="survey.enabled ? $t('admin_memorable_moment.enable') : $t('admin_memorable_moment.disable')"></v-text-field>
        </v-col>
        <v-col cols="3">
          <v-btn block
                 color="primary"
                 v-if="!survey.enabled"
                 @click="enableSurvey">{{ $t('admin_memorable_moment.enable') }}
          </v-btn>
          <v-btn block
                 color="primary"
                 v-if="survey.enabled"
                 @click="disableSurvey">{{ $t('admin_memorable_moment.disable') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field outlined
                        :label="$t('admin_event_survey.title_en')"
                        v-model="survey.title_en"
                        :rules="requiredRules"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field outlined
                        :label="$t('admin_event_survey.title_zh')"
                        v-model="survey.title_zh"
                        :rules="requiredRules"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-input hide-details
                   v-model="survey.description_en">
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_event_survey.description_en') }}</legend>
              <TinyMceEditor v-model="survey.description_en"></TinyMceEditor>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-input hide-details
                   v-model="survey.description_zh">
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_event_survey.description_zh') }}</legend>
              <TinyMceEditor v-model="survey.description_zh"></TinyMceEditor>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-input hide-details>
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_event_survey.questions') }}</legend>

              <draggable :list="questions"
                         draggable=".question"
                         handle=".question-handle"
                         @change="onQuestionMoved"
                         class="v-expansion-panels v-item-group theme--light draggable-list flex-1">
                <div class="question v-expansion-panel flex-column"
                     v-for="(question, idx) in questions"
                     :key="question.id">
                  <v-row class="v-expansion-panel-header">
                    <v-btn icon
                           class="question-handle">
                      <v-icon class="question-handle">fas fa-grip-lines</v-icon>
                    </v-btn>
                    <v-col class="question-title"
                           @click="editingQuestionIndex = idx">
                      <v-row v-if="idx !== editingQuestionIndex">
                        <v-col>
                          {{ question.title_en }}
                        </v-col>
                      </v-row>
                      <v-row v-if="idx !== editingQuestionIndex">
                        <v-col>
                          {{ question.title_zh }}
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-btn icon
                           class="question-expand"
                           v-if="idx !== editingQuestionIndex"
                           @click="editingQuestionIndex = idx"
                           key="open">
                      <v-icon>fas fa-sort-down</v-icon>
                    </v-btn>
                    <v-btn icon
                           class="question-expand"
                           v-if="idx === editingQuestionIndex"
                           @click="editingQuestionIndex = -1"
                           key="close">
                      <v-icon>fas fa-sort-up</v-icon>
                    </v-btn>
                  </v-row>
                  <v-container v-show="idx === editingQuestionIndex">
                    <v-row>
                      <v-col>
                        <v-text-field readonly
                                      :label="$t('admin_event_survey.question_type')"
                                      :value="$t('admin_event_survey.' + question.type)"></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-text-field outlined
                                      :label="$t('admin_event_survey.question_title_en')"
                                      v-model="question.title_en"></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-text-field outlined
                                      :label="$t('admin_event_survey.question_title_zh')"
                                      v-model="question.title_zh"></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row v-if="question.type === 'rating'">
                      <v-col>
                        <v-text-field outlined
                                      :label="$t('admin_event_survey.question_min_label_en')"
                                      v-model="question.min_label_en"></v-text-field>
                      </v-col>
                      <v-col>
                        <v-text-field outlined
                                      :label="$t('admin_event_survey.question_max_label_en')"
                                      v-model="question.max_label_en"></v-text-field>
                      </v-col>
                      <v-col>
                        <v-text-field outlined
                                      :label="$t('admin_event_survey.question_min_label_zh')"
                                      v-model="question.min_label_zh"></v-text-field>
                      </v-col>
                      <v-col>
                        <v-text-field outlined
                                      :label="$t('admin_event_survey.question_max_label_zh')"
                                      v-model="question.max_label_zh"></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-checkbox :label="$t('admin_event_survey.question_required')"
                                    v-model="question.required"></v-checkbox>
                      </v-col>
                    </v-row>
                    <v-divider></v-divider>
                    <v-row>
                      <v-col class="text-right">
                        <v-tooltip top>
                          <template #activator="{on, attrs}">
                            <v-btn icon
                                   v-on="on"
                                   v-bind="attrs"
                                   @click="cloneQuestion(idx)">
                              <v-icon>fas fa-clone</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('admin_event_survey.clone_question') }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                          <template #activator="{on, attrs}">
                            <v-btn icon
                                   v-on="on"
                                   v-bind="attrs"
                                   @click="deleteQuestion(idx)">
                              <v-icon>fas fa-trash</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('admin_event_survey.delete_question') }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </v-container>
                </div>
              </draggable>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row>
        <!--        <v-col>-->
        <!--          <v-btn block-->
        <!--                 @click="addQuestion('single_line')">{{ $t('admin_survey.add_single_line_question') }}-->
        <!--          </v-btn>-->
        <!--        </v-col>-->
        <v-col>
          <v-btn block
                 @click="addQuestion('freetext')">{{ $t('admin_event_survey.add_freetext_question') }}
          </v-btn>
        </v-col>
        <v-col>
          <v-btn block
                 @click="addQuestion('rating')">{{ $t('admin_event_survey.add_rating_question') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import _ from 'lodash';
import draggable from 'vuedraggable';
import {ref, onMounted} from "@vue/composition-api";
import TinyMceEditor from "@/components/TinyMceEditor";
import uuid4 from 'uuid/v4';

export default {
  name: 'EventSurvey',
  components: {TinyMceEditor, draggable},
  props: {
    eventId: String,
    canSave: Boolean,
    save: Function,
  },
  setup(props, {root, emit}) {
    onMounted(() => {
      getSurvey();

      emit('update:save', save);
    })

    const isFormValid = ref(false);
    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);

    const survey = ref(null);
    const getSurvey = function () {
      root.$callAPI('hkiarace/admin/event/survey/', {event_id: props.eventId}, function (response) {
        survey.value = {
          title: '',
          description: '',

          ...response.body.survey,
          enabled: response.body.enabled,
        };

        questions.value = response.body.survey ? _.sortBy(response.body.survey.questions, 'order') : [];
      })
    }

    const enableSurvey = function () {
      root.$callAPI('hkiarace/admin/event/survey/enable/', {event_id: props.eventId}, () => {
        getSurvey();
      })
    }
    const disableSurvey = function () {
      root.$callAPI('hkiarace/admin/event/survey/disable/', {event_id: props.eventId}, () => {
        getSurvey();
      })
    }

    const questions = ref(null);
    const editingQuestionIndex = ref(0);
    const addQuestion = function (type) {
      questions.value.push({
        id: uuid4(),
        title_en: 'Question',
        title_zh: '問題',
        type: type,
        required: false,
        min_label_en: 'Min',
        max_label_en: 'Max',
        min_label_zh: '最小',
        max_label_zh: '最大',
      });
    }
    const onQuestionMoved = function (e) {
      if (e.moved) {
        editingQuestionIndex.value = e.moved.newIndex;
      }
    }
    const cloneQuestion = function (idx) {
      questions.value.splice(idx + 1, 0,
          {
            ...questions.value[idx],
            id: uuid4(),
          }
      )
      editingQuestionIndex.value = idx + 1;
    }
    const deleteQuestion = function (idx) {
      questions.value.splice(idx, 1);
      editingQuestionIndex.value = -1;
    }

    const save = function () {
      root.$callAPI('hkiarace/admin/event/survey/set/', {
        event_id: props.eventId,
        ...survey.value,
        questions: questions.value.map(function (q, index) {
          return {
            ...q,
            order: index,
          }
        }),
      }, () => {
        getSurvey();
        root.$showAlertMessage(root.$t('admin_event_survey.save_success_msg'))
      })
    };

    return {
      isFormValid,
      requiredRules,

      survey,

      enableSurvey,
      disableSurvey,

      questions,
      editingQuestionIndex,
      addQuestion,
      onQuestionMoved,
      cloneQuestion,
      deleteQuestion,
    }
  },
}
</script>

<style lang="less">
.event-survey {
  .question {
    .question-handle, .question-expand {
      flex: 0 0 auto;
    }

    .question-title {
      flex: 1 0 0 !important;
      padding-top: 16px;
    }
  }
}
</style>
